import type { BootContext, BootStrategy } from "./types";
import {
  registerAllLayerAResources,
  finalizeAllLayerAResources,
  getLayerBStores,
  purgeLegacyReceiptOrderCache,
} from "./registry";
import {
  syncAllVersionsFromNetwork,
  revalidateResourceByData,
} from "~/services/repos/versionSync";
import { useAuthStore } from "~/stores/auth";

/**
 * Full online boot. Steps:
 *  1. Auth: hydrate from Firestore (with localStorage fast-path fallback
 *     inside `hydrateFromFirestore`).
 *  2. Layer A: register every version-tracked resource, then reconcile
 *     against the Firestore version doc (fetch on hash drift / cache hit
 *     otherwise), finalize.
 *  3. Layer B: each runtime store seeds its `knownKeys` dedup set from
 *     IDB keys and subscribes RTDB. `bootOnline` resolves on first RTDB
 *     settle (or short timeout) — no more 4s magic.
 */
export class OnlineBootStrategy implements BootStrategy {
  readonly mode = "online" as const;

  async loadAuth(): Promise<void> {
    await useAuthStore().hydrateFromFirestore();
    // Background, online-only: refresh Role / Locale / AccessList from the API over
    // the login-time snapshot so an admin's edit takes effect on reload without a
    // full re-login. Fire-and-forget — never blocks boot; the route guard picks up
    // a changed AccessList on the next navigation. OfflineBootStrategy never calls
    // this, so offline boot issues no network request.
    void useAuthStore().refreshCurrentUserProfile();
  }

  async loadLayerA(_ctx: BootContext): Promise<void> {
    registerAllLayerAResources();
    await syncAllVersionsFromNetwork();
    await finalizeAllLayerAResources();
    // Background, online-only: correct out-of-band `customerInfo` drift (e.g. a
    // renewed `ExpireDate`) without blocking boot. Fire-and-forget so route push
    // and Layer B never wait on it. `OfflineBootStrategy` never calls this, so
    // offline-first boot issues no network request.
    void revalidateResourceByData("customerInfo");
  }

  async bootLayerB(ctx: BootContext): Promise<void> {
    purgeLegacyReceiptOrderCache();
    await Promise.all(getLayerBStores().map((s) => s.bootOnline(ctx)));
  }
}
